import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloseIcon } from "../../../../assets/icons";
import { TickIcon } from "../../../../assets/icons/TickIcon";
import { UntickedIcon } from "../../../../assets/icons/UntickedIcon";
import { updateRC } from "../../../../backend-services";
import { InputComponent } from "../../../core/Forms";
import RegistrationCodeInput from "../../../core/Forms/RegistrationCodeInput";
import RegistrationCodeSection from "../../../core/Forms/RegistrationCodeInput/RegistrationCodeSection";
import { verifyCode } from "../../../../utils/verifyCode";
import { DEBOUNCE_DELAY } from "../../../../constants/todo";

interface GeneratePaymentPromptProps {
  onGenerate: (event: any) => void;
  onClose?: () => void;
  showVerificationPrompt: boolean;
  setShowSideModal: (show: boolean) => void;
  onVerifySuccess: () => void;
}

interface Option {
  code: string;
  label: string;
}

const RCVerificationModal: React.FC<GeneratePaymentPromptProps> = ({
  onGenerate,
  onClose,
  showVerificationPrompt,
  setShowSideModal,
  onVerifySuccess,
}) => {
  const navigate = useNavigate();
  const [registrationCode, setRegistrationCode] = useState<string>("");
  const [rcPrefix, setRcPrefix] = useState("");
  const [rcType, setRcType] = useState("");
  const [rcCheck, setRcCheck] = useState(Boolean);
  const [error, setError] = useState<string>("");
  const [verificationMessage, setVerificationMessage] = useState<string | null>(
    null,
  );
  const [showVerificationModal, setShowVerificationModal] = useState(
    showVerificationPrompt,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegistrationCode(e.target.value);
    setVerificationMessage(null);
  };

  const handleRcSave = () => {
    setIsLoading(true);
    setRcCheck(!rcCheck);
  };

  const validCodeLength = [8, 9, 10];
  const isButtonDisabled = !validCodeLength.includes(registrationCode.length);
  useEffect(() => {
    const handleRcVerification = async () => {
      if (validCodeLength.includes(registrationCode.length)) {
        setVerificationMessage("Verifying registration code...");
        const result = await verifyCode({
          rcnumber: registrationCode,
          businessType: rcType,
        });
        setIsLoading(false);
        setVerificationMessage(result);
        if (result == "Registration code verified") {
          onVerifySuccess();
          setShowVerificationModal(true);
        }
      } else {
        setVerificationMessage(null);
      }
    };

    if (validCodeLength.includes(registrationCode.length)) {
      const handler = setTimeout(handleRcVerification, DEBOUNCE_DELAY);
      return () => clearTimeout(handler);
    } else {
      setVerificationMessage(null);
    }
  }, [rcCheck]);

  const [isSelected, setIsSelected] = useState(false);
  const handleOptionSelect = () => {
    setIsSelected(true);
  };

  const handleRegistrationCodeChange = (option: Option) => {
    setRcPrefix(option.code);
    setRcType(option.label);
    setRegistrationCode("");
    setVerificationMessage(null);
    setError("");
  };

  return (
    <div className="bg-gray-900 bg-opacity-30 fixed top-0 right-0 left-0 z-50 h-[100vh] flex items-center justify-center p-4">
      <div className="bg-white rounded-[16px] min-w-[340px] sm:w-[450px] md:w-[570px] md:p-6 p-4">
        <div className="relative flex justify-between">
          <h3 className="text-[18px] md:text-[20px] leading-6 font-semibold">
            Business Registration Code
          </h3>
          <CloseIcon
            className="absolute md:top-[-7px] right-0 w-[30px] flex justify-center items-center p-1 cursor-pointer"
            onClick={onClose}
          />
        </div>
        <p className="mt-2 mr-10 mb-4 md:text-[14px] text-[12px]">
          Enter your business registration code below for verification.
        </p>

        <div role="form" onSubmit={(e) => e.preventDefault()}>
          <div className="w-full">
            <RegistrationCodeInput
              id="businessType"
              name="businessType"
              value={rcType}
              onChange={handleRegistrationCodeChange}
              onSelect={handleOptionSelect}
              error={error}
              isSlideUp={false}
            />
            {isSelected && (
              <RegistrationCodeSection
                id="businessType"
                name="businessType"
                value={registrationCode}
                rcPrefix={rcPrefix}
                onChange={(e) => {
                  setRegistrationCode(e.target.value);
                  handleInputChange(e);
                }}
                verificationResult={verificationMessage}
              />
            )}
          </div>
        </div>
        <div className="w-full flex items-center md:gap-4 gap-2 mt-6">
          <button
            className="flex-1 flex items-center justify-center rounded-md md:h-[46px] h-[38px] font-semibold text-[#044E97] border border-gray-300 hover:bg-slate-100"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={`flex-1 rounded-md md:h-[46px] h-[38px] ${
              isButtonDisabled
                ? "bg-[#044d9753] text-slate-500"
                : "bg-[#044E97] text-[white]"
            }`}
            onClick={handleRcSave}
            disabled={isButtonDisabled || isLoading} // Disable button when loading
          >
            {isLoading ? "Loading..." : "Save"} {/* Show loading text */}
          </button>
        </div>
      </div>
    </div>
  );
};

export { RCVerificationModal };
